import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { sendFailureNotification } from "shared/toast_notifications"
import { getAllParts } from "shared/device_types/parts"
import { allPartsStore } from "../../../stores/parts/all_parts_store"

// Connects to data-controller="suppliers--device-types--parts-supplier"
export default class extends Controller {

    static stores = [
        allPartsStore
    ]

    static values = {
        autoLoad: Boolean
    }

    static targets = [
        "loadingIndicator"
    ]

    connect() {
        useStore(this)

        // If autoLoad is enabled, load parts immediately
        if (this.autoLoadValue) {
            this.loadParts()
        }
    }

    /**
     * Load all parts and populate the allPartsStore
     */
    async loadParts() {
        try {
            // Show loading state
            this.setLoadingState(true)

            // Signal the shared argus--table loading overlay (Path A′).
            // allPartsStore carries data only (no loading boolean), and the
            // fetch lives here in the supplier, so the supplier emits the
            // loading signal directly to the Parts table's argus--table.
            this._getArgusTable()?.showLoading()

            // Fetch all parts using the service
            const parts = await getAllParts()

            // Update the store with fetched parts
            this.setAllPartsStoreValue(parts)
        } catch (error) {
            console.error('Error loading parts:', error)

            // Show user-friendly error message
            sendFailureNotification('Unable to load parts. Please try again.')

            // Clear parts on error
            this.setAllPartsStoreValue([])
        } finally {
            // Hide loading state
            this.setLoadingState(false)

            // Clear the shared loading overlay on both success and error
            this._getArgusTable()?.hideLoading()
        }
    }

    /**
     * Resolve the sibling argus--table controller for the Parts table.
     *
     * The Parts table element carries both the `table--parts-table` and
     * `argus--table` controllers (see settings/parts/index.html.erb). This
     * supplier lives on the outer container, so locate that element within
     * the supplier's scope and resolve its argus--table controller instance
     * via getControllerForElementAndIdentifier.
     */
    _getArgusTable() {
        const tableElement = this.element.querySelector(
            '[data-controller~="argus--table"]'
        )
        if (!tableElement) return null
        return this.application.getControllerForElementAndIdentifier(
            tableElement, 'argus--table'
        )
    }

    /**
     * Manually refresh parts
     */
    refreshParts() {
        this.loadParts()
    }

    /**
     * Clear all parts from the store
     */
    clearParts() {
        this.setAllPartsStoreValue([])
    }

    /**
     * Set loading state and update UI if loading indicator target exists
     */
    setLoadingState(isLoading) {
        if (this.hasLoadingIndicatorTarget) {
            if (isLoading) {
                this.loadingIndicatorTarget.style.display = 'block'
            } else {
                this.loadingIndicatorTarget.style.display = 'none'
            }
        }
    }
}